// =============================================================================
// src/lib/api.ts  —  Central authenticated fetch helper
// =============================================================================
// Single source of truth for all authenticated API calls in this app.
// Every request to the backend should go through apiFetch() so that auth
// token handling, Content-Type defaulting, and error parsing stay consistent.
//
// ── USAGE ─────────────────────────────────────────────────────────────────────
//
//   import { apiFetch } from 'src/lib/api';
//
//   // GET
//   const data = await apiFetch<MyType>('/cmas/history?limit=10');
//
//   // POST with a JSON body
//   const result = await apiFetch<ResultType>('/cmas', {
//     method: 'POST',
//     body: JSON.stringify({ formatted_address: '...' }),
//   });
//
// ── BEHAVIOUR ─────────────────────────────────────────────────────────────────
//   - Reads VITE_API_URL from .env for the base URL
//   - Gets the Cognito idToken from the active Amplify session
//   - Automatically sets Content-Type: application/json if the request has a
//     body and the caller hasn't already set it (skips FormData)
//   - Throws if the response is not ok, using the response body as the message
//   - Returns the parsed JSON body cast to T
//
// ── WHO USES THIS ─────────────────────────────────────────────────────────────
//   src/api/cmaHistory.ts          → fetchCmaHistory()
//   src/api/cmaPipeline.ts         → startCmaPipeline(), getCmaPipelineStatus()
//   src/pages/dashboard/dashboard.tsx → loadMe()
//   src/components/CompleteProfileModal.tsx
//   src/sections/users/view.tsx
// =============================================================================

import { fetchAuthSession } from 'aws-amplify/auth';

import { Sentry } from 'src/lib/sentry';

const API_BASE = import.meta.env.VITE_API_URL as string;

// ----------------------------------------------------------------------
// apiFetch<T>
// ----------------------------------------------------------------------
// Authenticated wrapper around fetch(). Prepends API_BASE to the path,
// attaches the Cognito idToken, and throws on non-2xx responses.
//
// path     — relative path e.g. '/cmas/history?limit=10'
// init     — standard RequestInit (method, body, headers, etc.)
// returns  — parsed JSON cast to T
// ----------------------------------------------------------------------
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  if (!API_BASE) throw new Error('Missing VITE_API_URL — check your .env file');

  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated — no idToken found');

  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${token}`);

  // Auto-set JSON content type if there's a body and the caller didn't set one
  // Skip for FormData — the browser sets the correct multipart boundary itself
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const text = await res.text();

  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const msg =
      (data as any)?.message ||
      (data as any)?.errors?.[0]?.message ||
      text ||
      `HTTP ${res.status}`;
    Sentry.withScope((scope) => {
      scope.setTag('http.status', res.status);
      scope.setContext('api', { path, method: init.method ?? 'GET', status: res.status, response: text.slice(0, 500) });
      // 4xx are agent/validation errors, not bugs — log as warning not error
      const level = res.status >= 500 ? 'error' : 'warning';
      scope.setLevel(level);
      Sentry.captureMessage(`API ${init.method ?? 'GET'} ${path} → ${res.status}`, level);
    });
    throw new Error(msg);
  }

  return data as T;
}
